import type { FormatMessages } from "@skilitics/localization";

import type { ElementState } from "./types.ts";

/** Type that requires a Label for each ElementState, with optional Tooltip */
type ElementStateFormatStrings = {
  [K in ElementState as `${K}Label`]: { en: string };
} & {
  [K in ElementState as `${K}Tooltip`]?: { en: string };
} & FormatMessages;

export const elementStateFormatStrings: ElementStateFormatStrings = {
  // Base states
  defaultLabel: { en: "Default" },
  defaultTooltip: { en: "The default appearance of the element." },
  hoverLabel: { en: "Hover" },
  hoverTooltip: { en: "Applied when the user hovers over the element." },
  focusLabel: { en: "Focus" },
  focusTooltip: { en: "Applied when the element has keyboard focus." },
  activeLabel: { en: "Active" },
  activeTooltip: { en: "Applied when the element is being clicked or activated." },
  disabledLabel: { en: "Disabled" },
  disabledTooltip: { en: "Applied when the element is disabled." },

  // Draggable states
  draggingLabel: { en: "Dragging" },
  draggingTooltip: { en: "Applied while the element is actively being dragged." },
  dragPlaceholderLabel: { en: "Drag Placeholder" },
  dragPlaceholderTooltip: { en: "Applied to the source element left behind during a clone drag operation." },
  draggingAllowedLabel: { en: "Dragging Allowed" },
  draggingAllowedTooltip: { en: "Applied while dragging and over a droppable that accepts this item." },
  draggingNotAllowedLabel: { en: "Dragging Not Allowed" },
  draggingNotAllowedTooltip: { en: "Applied while dragging and over a droppable that does not accept this item." },
  dragCorrectLabel: { en: "Drag Correct" },
  dragCorrectTooltip: { en: "Applied in practice mode when the element is placed in the correct location." },
  dragIncorrectLabel: { en: "Drag Incorrect" },
  dragIncorrectTooltip: { en: "Applied in practice mode when the element is placed in an incorrect location." },
  dragDroppedLabel: { en: "Drag Dropped" },
  dragDroppedTooltip: { en: "Applied after the element has been successfully dropped in another container." },

  // Droppable states
  dropTargetLabel: { en: "Drop Target" },
  dropTargetTooltip: { en: "Applied when a draggable is currently over this droppable." },
  droppableAllowedLabel: { en: "Droppable Allowed" },
  droppableAllowedTooltip: { en: "Applied when the current dragged item would be accepted by this droppable." },
  droppableNotAllowedLabel: { en: "Droppable Not Allowed" },
  droppableNotAllowedTooltip: { en: "Applied when the current dragged item would not be accepted by this droppable." },
  droppableOccupiedLabel: { en: "Droppable Occupied" },
  droppableOccupiedTooltip: { en: "Applied when the droppable has at least one draggable item in it." },
  droppableActiveLabel: { en: "Droppable Active" },
  droppableActiveTooltip: { en: "Applied when a draggable is over this droppable and allowed to drop." },

  // HotZone states
  hotZoneActiveLabel: { en: "Hot Zone Active" },
  hotZoneActiveTooltip: { en: "Applied when a draggable is colliding with this hot zone and its tags are accepted." },
  hotZoneAllowedLabel: { en: "Hot Zone Allowed" },
  hotZoneAllowedTooltip: { en: "Applied when the current dragged item's tags would be accepted by this hot zone." },
  hotZoneNotAllowedLabel: { en: "Hot Zone Not Allowed" },
  hotZoneNotAllowedTooltip: {
    en: "Applied when the current dragged item's tags would not be accepted by this hot zone.",
  },
};
